import type { Command } from 'prosemirror-state';

import { ACTION, ACTION_SUBJECT, EVENT_TYPE } from '@atlaskit/editor-common/analytics';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import AiGenerativeRemixIcon from '@atlaskit/icon-lab/core/ai-generative-remix';
import { expVal } from '@atlaskit/platform-feature-experiments/exp-val';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';

import type {
	GetRemixFloatingToolbarButton,
	MalleableUiPlugin,
	RemixDisabledReason,
} from '../../../malleableUiPluginType';
import { messages } from '../../messages';
import {
	isRemixInlineDropdownVariant,
	REMIX_FLOATING_TOOLBAR_ANCHOR_CLASS,
} from '../../remix-config';
import { getRemixDisabledTooltip } from '../../remix-disabled-tooltip';
import { getEditorRemixLifecyclePresentation } from '../../utils/editor-remix-callback-registry';

const remixFloatingToolbarClickAnalyticsPayload = {
	action: ACTION.CLICKED,
	actionSubject: ACTION_SUBJECT.BUTTON,
	actionSubjectId: 'remixModalFromToolbar',
	eventType: EVENT_TYPE.UI,
	source: 'remix',
} as const;

/**
 * Factory that returns the `getRemixFloatingToolbarButton` action.
 *
 * Builds a floating-toolbar item that renders a button that opens the Remix modal.
 */
export function makeRemixFloatingToolbarButton({
	api,
	options,
}: {
	api?: ExtractInjectionAPI<MalleableUiPlugin>;
	options?: {
		isDisabled?: boolean;
		isRemixHidden?: boolean;
		remixDisabledReason?: RemixDisabledReason;
	};
}): GetRemixFloatingToolbarButton {
	return ({ node, intl }) => {
		if (!api || !node) {
			return undefined;
		}

		if (options?.isRemixHidden) {
			return undefined;
		}

		if (!fg('platform_editor_ai_remix_toolbar') || !fg('maui_remixable_page_native_embeds')) {
			return undefined;
		}

		// A machine-owned flow is a Remix in progress even though the projection reports
		// `!isThinking` once the early streaming whiteboard lands. Without this the button reads as
		// disabled with no reason and the tooltip helper's default renders the archived copy.
		const isRemixing =
			api?.malleableUi?.sharedState.currentState()?.isRemixModalOpen ||
			api?.malleableUi?.sharedState.currentState()?.isThinking ||
			getEditorRemixLifecyclePresentation(api).phase === 'inserting';
		const areAiEntryPointsDisabled =
			api?.aiExperience?.sharedState.currentState()?.areAiEntryPointsDisabled;
		const disabled = options?.isDisabled || isRemixing || areAiEntryPointsDisabled;

		// cc_maui_create_keyword: when enabled, swap the "Remix" keyword for "Create".
		const isCreateKeywordEnabled = expValEquals('cc_maui_create_keyword', 'isEnabled', true);

		let tooltipContent = intl.formatMessage(
			isCreateKeywordEnabled ? messages.createToolbarTooltip : messages.remixToolbarTooltip,
		);
		if (isRemixing) {
			tooltipContent = intl.formatMessage(
				isCreateKeywordEnabled ? messages.createInProgressTooltip : messages.remixInProgressTooltip,
			);
		} else if (options?.remixDisabledReason) {
			tooltipContent = getRemixDisabledTooltip(intl.formatMessage, options?.remixDisabledReason);
		} else if (fg('cc_maui_updated_remix_tooltip_text')) {
			tooltipContent = intl.formatMessage(
				isCreateKeywordEnabled
					? messages.createBlockTooltipUpdated
					: messages.remixToolbarTooltipUpdated,
			);
		}

		const onClick: Command = (editorState) => {
			if (disabled) {
				return false;
			}
			expVal('cc-ce-maui-remix-menu-multivariant', 'cohort', 'control');
			expVal('cc-ce-maui-remix-menu-multivariant-aa', 'variant', 'control');

			const $pos = editorState.selection.$from;
			let nodePos: number | undefined;
			for (let depth = $pos.depth; depth > 0; depth--) {
				if ($pos.node(depth) === node) {
					nodePos = $pos.before(depth);
					break;
				}
			}
			api.analytics?.actions.fireAnalyticsEvent<
				typeof remixFloatingToolbarClickAnalyticsPayload,
				'customEventType'
			>(remixFloatingToolbarClickAnalyticsPayload);
			// Anchor the inline dropdown to the rendered Remix button: the floating toolbar
			// re-evaluates its items between render and click, so a captured `node` reference can be
			// stale, and the popup should anchor to the visible toolbar anyway. Several toolbars can
			// be mounted at once, so pick the first match that is actually visible.
			const anchorRect = isRemixInlineDropdownVariant()
				? Array.from(
						document.querySelectorAll<HTMLElement>(`.${REMIX_FLOATING_TOOLBAR_ANCHOR_CLASS}`),
					)
						.map((el) => el.getBoundingClientRect())
						.find((rect) => rect.width > 0 && rect.height > 0)
				: undefined;
			api.core.actions.execute(
				api.malleableUi.commands.openRemixModal({
					nodePos,
					nodeType: node.type.name,
					metadata: {
						interactionSource: 'remixModalFromFloatingToolbar',
						anchorRect,
					},
					source: 'floatingToolbar',
				}),
			);
			return true;
		};

		return {
			id: 'editor.malleable-ui.remix',
			type: 'button',
			title: intl.formatMessage(
				isCreateKeywordEnabled ? messages.createToolbarLabel : messages.remixToolbarLabel,
			),
			tooltipContent,
			showTitle: true,
			hideTooltipOnClick: false,
			icon: AiGenerativeRemixIcon,
			onClick,
			disabled,
			testId: 'editor-floating-toolbar__remix-button',
			...(isRemixInlineDropdownVariant() && { className: REMIX_FLOATING_TOOLBAR_ANCHOR_CLASS }),
		};
	};
}
